import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiMail, FiX, FiLoader } from 'react-icons/fi';
import api from '../../../api/api';
import { SetUser } from '../../../actions/SetUser';
import { to } from '../../../util/util';

interface Props {
    currentEmail: string;
    onClose: () => void;
    onChanged: (email: string) => void;
}

// two steps: code to the new address, then confirm — the sign-in email only changes on confirm
const ChangeEmailModal: React.FC<Props> = ({ currentEmail, onClose, onChanged }) => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const sendCode = async () => {
        setBusy(true);
        setError('');
        const [err] = await to(api().requestEmailChange(email.trim()));
        setBusy(false);
        if (err) setError(typeof err?.response?.data === 'string' ? err.response.data : 'Could not send the code');
        else setCodeSent(true);
    };

    const confirm = async () => {
        setBusy(true);
        setError('');
        const [err, result] = await to(api().confirmEmailChange(code.trim()));
        setBusy(false);
        if (err) {
            setError(typeof err?.response?.data === 'string' ? err.response.data : 'Could not change your email');
            return;
        }
        dispatch(SetUser(result.user));
        onChanged(result.user.username);
    };

    const canSend = !busy && /\S+@\S+\.\S+/.test(email);
    const canConfirm = !busy && /^\d{6}$/.test(code.trim());

    return <div className="modal-overlay" onClick={onClose}>
        <div className="change-email-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon-tile"><FiMail /></span>
                <div>
                    <h2>Change Email Address</h2>
                    <p>You sign in with this address. Currently {currentEmail}</p>
                </div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>

            <div className="modal-body">
                <label className="field">
                    <span className="label">New email address</span>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy || codeSent} autoFocus />
                </label>
                {codeSent && <label className="field">
                    <span className="label">6-digit code <em>sent to {email.trim()}, expires in 10 minutes</em></span>
                    <input inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} disabled={busy} autoFocus />
                </label>}
                {error && <p className="error">{error}</p>}
            </div>

            <div className="modal-footer">
                {codeSent && <button type="button" className="link resend" onClick={sendCode} disabled={busy}>Resend code</button>}
                <button type="button" className="link" onClick={onClose} disabled={busy}>Cancel</button>
                {codeSent
                    ? <button type="button" className="btn-primary" onClick={confirm} disabled={!canConfirm}>{busy && <FiLoader className="spin" />} {busy ? 'Confirming…' : 'Confirm'}</button>
                    : <button type="button" className="btn-primary" onClick={sendCode} disabled={!canSend}>{busy && <FiLoader className="spin" />} {busy ? 'Sending…' : 'Send Code'}</button>}
            </div>
        </div>
    </div>;
};

export default ChangeEmailModal;
